import React, { useEffect, useState } from 'react';
import { getActiveMatter } from '../lib/activeMatter';
import { Link, useSearchParams } from 'react-router-dom';
import { toast } from 'sonner';
import { ArrowLeft, GraduationCap, Loader2, MessageSquare, Scale, Heart, Briefcase, Gavel, Calculator, Building2, Lightbulb, Home, ScrollText, Landmark, Shield, Lock, Users, ShoppingCart, PiggyBank, Plane, Leaf, Zap, Droplets, Banknote, TrendingUp, Umbrella, Globe, KeyRound, Search } from 'lucide-react';
import { useAuth } from '../context/AuthContext';
import { CountrySelect, inputCls } from '../components/family/shared';
import { AgentChat } from '../components/specialists/AgentChat';
import { UpgradeDialog } from '../components/specialists/UpgradeDialog';
import { TeamReviewPanel } from '../components/specialists/TeamReviewPanel';

const ICONS = { scale: Scale, heart: Heart, briefcase: Briefcase, gavel: Gavel, calculator: Calculator, building: Building2, lightbulb: Lightbulb, home: Home, scroll: ScrollText, landmark: Landmark, shield: Shield, cart: ShoppingCart, piggy: PiggyBank, plane: Plane, leaf: Leaf, zap: Zap, droplets: Droplets, banknote: Banknote, trending: TrendingUp, umbrella: Umbrella, lock: KeyRound, globe: Globe };

const AreaGrid = ({ areas, value, onChange, onLocked }) => (
  <div className="grid grid-cols-2 gap-2">
    {areas.map((a) => {
      const Icon = ICONS[a.icon] || Scale;
      return (
        <button key={a.id} type="button" data-testid={`agent-area-${a.id}`} data-locked={a.locked ? 'true' : 'false'} onClick={() => (a.locked ? onLocked() : onChange(a.id))} title={a.focus}
          className={`flex items-center gap-2 rounded-xl border px-3 py-2 text-left text-sm transition-colors ${a.locked ? 'border-slate-800 text-slate-500 hover:bg-slate-800/50' : value === a.id ? 'border-indigo-400 bg-indigo-500/15 text-white' : 'border-slate-800 text-slate-300 hover:bg-slate-800'}`}>
          {a.locked ? <Lock className="h-4 w-4 shrink-0 text-amber-400/80" /> : <Icon className="h-4 w-4 shrink-0 text-indigo-300" />} {a.name}
        </button>
      );
    })}
  </div>
);

export default function SpecialistAgentsPage() {
  const { api } = useAuth();
  const [params] = useSearchParams();
  const [areas, setAreas] = useState([]);
  const [matters, setMatters] = useState([]);
  const [sessions, setSessions] = useState([]);
  const [area, setArea] = useState(params.get('area') || 'civil');
  const [country, setCountry] = useState(params.get('country') || 'chile');
  const [matterId, setMatterId] = useState(() => getActiveMatter()?.id || '');
  const [active, setActive] = useState(null);
  const [creating, setCreating] = useState(false);
  const [access, setAccess] = useState(null);
  const [upgrade, setUpgrade] = useState(null);
  const [teamMatter, setTeamMatter] = useState(null);
  const [areaQuery, setAreaQuery] = useState('');
  const norm = (x) => (x || '').normalize('NFD').replace(/[\u0300-\u036f]/g, '').toLowerCase();
  const shownAreas = areaQuery.trim() ? areas.filter((a) => norm(`${a.name} ${a.focus}`).includes(norm(areaQuery.trim()))) : areas;

  const loadAccess = () => api.get('/specialists/areas').then((r) => {
    setAreas(r.data.areas);
    setAccess(r.data.access);
    setArea((cur) => (r.data.access.allowed_areas.includes(cur) ? cur : r.data.access.allowed_areas[0]));
  }).catch(() => {});

  const loadSessions = () => api.get('/specialists/sessions').then((r) => setSessions(r.data.sessions)).catch(() => {});

  useEffect(() => {
    loadAccess();
    api.get('/crm/matters', { params: { limit: 100 } }).then((r) => setMatters(r.data.matters || [])).catch(() => {});
    loadSessions();
  }, [api]); // eslint-disable-line react-hooks/exhaustive-deps

  const start = async () => {
    setCreating(true);
    try {
      const { data } = await api.post('/specialists/sessions', { area, country, matter_id: matterId || null });
      setActive(data.id);
      setTeamMatter(null);
      loadSessions();
    } catch (err) {
      const d = err.response?.data?.detail;
      if (d?.code) setUpgrade(d.code);
      else toast.error(d || 'No se pudo iniciar la consulta');
    } finally {
      setCreating(false);
    }
  };

  return (
    <div className="min-h-screen bg-slate-950 text-white" data-testid="specialist-agents-page">
      <header className="border-b border-slate-800">
        <div className="mx-auto flex max-w-7xl items-center gap-3 px-4 py-4">
          <Link to="/dashboard" data-testid="agents-back-btn" className="text-slate-400 hover:text-white transition-colors"><ArrowLeft className="h-5 w-5" /></Link>
          <div className="flex h-10 w-10 items-center justify-center rounded-xl bg-indigo-500/15"><GraduationCap className="h-5 w-5 text-indigo-300" /></div>
          <div>
            <h1 className="text-lg font-bold font-['Outfit']">Agentes Especialistas IA</h1>
            <p className="text-xs text-slate-400">Socios senior por área y país, alimentados con tu CRM y Cerebro</p>
          </div>
        </div>
      </header>
      <main className="mx-auto grid max-w-7xl gap-6 px-4 py-8 lg:grid-cols-[360px_1fr]">
        <aside className="space-y-5">
          <div className="space-y-4 rounded-2xl border border-slate-800 bg-slate-900/60 p-5">
            <div>
              <span className="mb-1 block text-xs font-medium uppercase tracking-wider text-slate-400">País</span>
              <CountrySelect value={country} onChange={setCountry} testId="agents-country-select" />
            </div>
            <div>
              <span className="mb-2 block text-xs font-medium uppercase tracking-wider text-slate-400">Área del derecho</span>
              <div className="relative mb-2">
                <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-500" />
                <input data-testid="agents-area-search" value={areaQuery} onChange={(e) => setAreaQuery(e.target.value)} placeholder="Buscar por tema: despido, visa, marca, herencia…"
                  className="w-full rounded-lg border border-slate-700 bg-slate-950/60 py-2 pl-9 pr-3 text-sm text-white placeholder:text-slate-500 focus:border-indigo-400 focus:outline-none" />
              </div>
              {shownAreas.length === 0 && <p data-testid="agents-area-empty" className="text-xs text-slate-500">Ningún área coincide con «{areaQuery}».</p>}
              <AreaGrid areas={shownAreas} value={area} onChange={setArea} onLocked={() => setUpgrade('area_locked')} />
            </div>
            <div>
              <span className="mb-1 block text-xs font-medium uppercase tracking-wider text-slate-400">Caso del CRM (opcional)</span>
              <select data-testid="agents-matter-select" value={matterId} onChange={(e) => setMatterId(e.target.value)} className={inputCls}>
                <option value="">Sin caso específico</option>
                {matters.map((m) => <option key={m.id} value={m.id}>{m.title}</option>)}
              </select>
            </div>
            {access?.monthly_limit != null && (
              <p data-testid="agents-usage" className={`text-xs ${access.remaining === 0 ? 'text-amber-300' : 'text-slate-400'}`}>
                Plan gratuito: {access.used_this_month} de {access.monthly_limit} consultas usadas este mes
              </p>
            )}
            <button data-testid="agents-start-btn" onClick={start} disabled={creating}
              className="inline-flex w-full items-center justify-center gap-2 rounded-full bg-indigo-500 py-2.5 text-sm font-semibold text-white hover:bg-indigo-400 transition-colors disabled:opacity-60">
              {creating && <Loader2 className="h-4 w-4 animate-spin" />} Iniciar consulta
            </button>
            <button data-testid="agents-team-btn" onClick={() => setTeamMatter(matterId)} disabled={!matterId}
              title={matterId ? '' : 'Elige un caso del CRM'}
              className="inline-flex w-full items-center justify-center gap-2 rounded-full border border-indigo-400/60 py-2.5 text-sm font-semibold text-indigo-200 hover:bg-indigo-500/10 transition-colors disabled:opacity-40">
              <Users className="h-4 w-4" /> Equipo de Socios {matterId ? '' : '(elige un caso)'}
            </button>
          </div>
          {sessions.length > 0 && (
            <div className="rounded-2xl border border-slate-800 bg-slate-900/60 p-4" data-testid="agents-sessions-list">
              <p className="mb-2 text-xs font-medium uppercase tracking-wider text-slate-400">Consultas anteriores</p>
              <div className="max-h-72 space-y-1 overflow-y-auto">
                {sessions.map((s) => (
                  <button key={s.id} data-testid={`agents-session-${s.id}`} onClick={() => { setActive(s.id); setTeamMatter(null); }}
                    className={`flex w-full items-start gap-2 rounded-lg px-2 py-2 text-left text-sm transition-colors ${active === s.id ? 'bg-slate-800 text-white' : 'text-slate-300 hover:bg-slate-800/60'}`}>
                    <MessageSquare className="mt-0.5 h-4 w-4 shrink-0 text-indigo-300" />
                    <span>{s.area_name} · {s.country}{s.matter_title ? ` · ${s.matter_title}` : ''}</span>
                  </button>
                ))}
              </div>
            </div>
          )}
        </aside>
        <section className="rounded-2xl border border-slate-800 bg-slate-900/60 p-5">
          {teamMatter ? <TeamReviewPanel key={teamMatter} matterId={teamMatter} country={country} areas={areas} onBlocked={setUpgrade} onDone={loadAccess} /> : active ? <AgentChat key={active} sessionId={active} onSent={() => { loadSessions(); loadAccess(); }} onBlocked={setUpgrade} /> : (
            <div className="flex h-96 flex-col items-center justify-center text-center text-slate-400" data-testid="agents-empty-state">
              <GraduationCap className="mb-3 h-10 w-10 text-indigo-300" />
              <p className="max-w-sm text-sm">Elige país, área y, si quieres, un caso de tu CRM. El socio especialista leerá el caso y el análisis de Cerebro antes de responder.</p>
            </div>
          )}
        </section>
      </main>
      {upgrade && <UpgradeDialog reason={upgrade} onClose={() => setUpgrade(null)} />}
    </div>
  );
}
